import type { ZoneBoundaryMode } from "@sitescene/types";
import type { Key } from "react-aria-components";

import {
	BezierCurve03,
	Cube01,
	Edit05,
	Lightning01,
	Trash01,
} from "@untitledui/icons";
import { useMemo, useState } from "react";

import {
	ButtonGroup,
	ButtonGroupItem,
} from "@/components/ui/button-group/button-group";
import { Button } from "@/components/ui/buttons/button";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { ColorPicker } from "@/components/ui/color-picker/color-picker";
import { Input } from "@/components/ui/input/input";
import { Slider } from "@/components/ui/slider/slider";
import { Tooltip } from "@/components/ui/tooltip/tooltip";
import { GeneralModal } from "@/features/modals/general-modal";

import type { ShadowFloor } from "../../../types/location-map";
import type { SensorAutoSaveStatus } from "../../../utils/sensor/sensor-auto-save";

import { ZoneFloorSelect } from "../../../components/forms/zone-floor-select";
import {
	getZoneBoundarySensorsOutsideMessage,
	isZoneEditDraftBoundarySelfIntersecting,
	MAX_ZONE_BORDER_WIDTH,
	MIN_ZONE_BORDER_WIDTH,
	normalizeZoneBorderWidth,
	type ShadowZoneEditDraft,
	ZONE_BOUNDARY_AUTO_MOVE_HINT,
	ZONE_BOUNDARY_CUSTOM_DRAW_HINT,
	ZONE_BOUNDARY_CUSTOM_EDIT_HINT,
	ZONE_BOUNDARY_CUSTOM_VERTEX_HINT,
	ZONE_BOUNDARY_EXPAND_TO_INCLUDE_LABEL,
	ZONE_BOUNDARY_MOVE_SENSORS_HINT,
	ZONE_BOUNDARY_MOVE_SENSORS_LABEL,
	ZONE_BOUNDARY_REDRAW_LABEL,
	ZONE_BOUNDARY_REMOVE_LABEL,
	ZONE_BOUNDARY_SELF_INTERSECTION_MESSAGE,
	ZONE_TYPE_LINE_HINT,
	ZONE_TYPE_SHAPE_HINT,
	type ZoneSensorOutsideBoundary,
} from "../../../utils/location/location-map-utils";
import { floorsForInstallation } from "../../../utils/location/zone-floor";
import { hasAutoSaveEdits } from "../../../utils/shared/auto-save-display";
import { toTitleCase } from "../../../utils/shared/strings";
import {
	DEFAULT_ZONE_COLOR,
	mergeZoneColorSwatches,
	normalizeZoneColor,
} from "../../../utils/zone/zone-colors";
import { EditScrollBody } from "./edit-scroll-body";

interface ZoneEditFormProps {
	autoSaveStatus?: SensorAutoSaveStatus;
	canUndoZoneEdit: boolean;
	editDraft: ShadowZoneEditDraft;
	floors?: ShadowFloor[];
	installationId?: string;
	isEditDirty: boolean;
	isUpdatedZonePending: boolean;
	moveSensorsWithOutline?: boolean;
	onCancel: () => void;
	onDraftChange: (updates: Partial<ShadowZoneEditDraft>) => void;
	onExpandBoundaryToIncludeSensors?: () => void;
	onHoverOutsideSensor?: (sensorId?: string) => void;
	onMoveSensorsWithOutlineChange?: (enabled: boolean) => void;
	onPreviewOutsideSensor?: (sensorId: string) => void;
	onSave: () => void;
	sensorsOutsideBoundary?: ZoneSensorOutsideBoundary[];
}

const firstSelectedKey = (keys: "all" | Set<Key>): Key | undefined => {
	if (keys === "all") return undefined;
	return [...keys][0];
};

const OUTLINE_MODE_HINT: Record<ZoneBoundaryMode, string> = {
	auto: ZONE_BOUNDARY_AUTO_MOVE_HINT,
	custom: ZONE_BOUNDARY_CUSTOM_EDIT_HINT,
};

/**
 * Inline form for renaming a zone and picking its map color.
 * Changes auto-save after a short pause; Done exits edit.
 */
export const ZoneEditForm = ({
	autoSaveStatus = "idle",
	canUndoZoneEdit,
	editDraft,
	floors = [],
	installationId,
	isEditDirty,
	isUpdatedZonePending,
	moveSensorsWithOutline = true,
	onCancel,
	onDraftChange,
	onExpandBoundaryToIncludeSensors,
	onHoverOutsideSensor,
	onMoveSensorsWithOutlineChange,
	onPreviewOutsideSensor,
	onSave,
	sensorsOutsideBoundary = [],
}: ZoneEditFormProps) => {
	const [extraSwatchColors, setExtraSwatchColors] = useState<string[]>([]);
	const [isAddColorModalOpen, setIsAddColorModalOpen] = useState(false);
	const [pendingCustomColor, setPendingCustomColor] =
		useState(DEFAULT_ZONE_COLOR);

	const swatchColors = useMemo(
		() => mergeZoneColorSwatches(extraSwatchColors),
		[extraSwatchColors],
	);

	const zoneFloors = useMemo(
		() => floorsForInstallation(floors, installationId),
		[floors, installationId],
	);

	const hasEdited = hasAutoSaveEdits({
		autoSaveStatus,
		canUndoEdit: canUndoZoneEdit,
		isEditDirty,
		isUpdatePending: isUpdatedZonePending,
	});
	const showDone = autoSaveStatus === "idle" || autoSaveStatus === "saved";
	const selectedColor = normalizeZoneColor(editDraft.color);
	const selectedBorderWidth = normalizeZoneBorderWidth(editDraft.borderWidth);
	const isBoundarySelfIntersecting =
		isZoneEditDraftBoundarySelfIntersecting(editDraft);
	const isShape = editDraft.boundaryKind !== "line";
	const hasDrawnOutline = Boolean(editDraft.boundary);
	const isAutoOutline = isShape && editDraft.boundaryMode === "auto";
	const canClearOutline =
		isAutoOutline ||
		(hasDrawnOutline && (!isShape || editDraft.boundaryMode === "custom"));
	const clearOutlineLabel = isAutoOutline
		? ZONE_BOUNDARY_REMOVE_LABEL
		: ZONE_BOUNDARY_REDRAW_LABEL;
	const showOutlineStyleControls = hasDrawnOutline || isAutoOutline;
	const showSensorsOutsideWarning =
		isShape &&
		editDraft.boundaryMode === "custom" &&
		!isBoundarySelfIntersecting &&
		sensorsOutsideBoundary.length > 0;

	const handleAddCustomColor = () => {
		const normalized = normalizeZoneColor(pendingCustomColor).toUpperCase();
		setExtraSwatchColors((current) =>
			current.includes(normalized) ? current : [...current, normalized],
		);
		onDraftChange({ color: normalized });
		setIsAddColorModalOpen(false);
	};

	const handleClearOutline = () => {
		if (isAutoOutline) {
			onDraftChange({ boundary: null, boundaryMode: "custom" });
			return;
		}
		onDraftChange({ boundary: null });
	};

	const handleZoneTypeChange = (keys: "all" | Set<Key>) => {
		const next = firstSelectedKey(keys);
		if (next !== "line" && next !== "polygon") return;
		if (next === editDraft.boundaryKind) return;
		onDraftChange({ boundaryKind: next });
	};

	const handleOutlineModeChange = (keys: "all" | Set<Key>) => {
		const next = firstSelectedKey(keys);
		if (next !== "auto" && next !== "custom") return;
		if (next === editDraft.boundaryMode) return;
		onDraftChange({ boundaryMode: next });
	};

	const zoneTypeKeys = useMemo(
		() => new Set([editDraft.boundaryKind]),
		[editDraft.boundaryKind],
	);
	const outlineModeKeys = useMemo(
		() => new Set([editDraft.boundaryMode]),
		[editDraft.boundaryMode],
	);

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
			<EditScrollBody>
				<div className="flex flex-col gap-3">
					<Input
						label="Zone Name"
						onChange={(value) => onDraftChange({ name: value })}
						placeholder="Enter zone name"
						size="sm"
						value={editDraft.name}
					/>
					<ZoneFloorSelect
						floors={zoneFloors}
						onChange={(floorId) => onDraftChange({ floorId })}
						size="sm"
						value={editDraft.floorId}
					/>

					<div className="flex flex-col gap-2">
						<p className="text-sm font-medium text-primary">Zone type</p>
						<div className="flex items-center gap-2">
							<ButtonGroup
								aria-label="Zone type"
								disallowEmptySelection
								onSelectionChange={handleZoneTypeChange}
								selectedKeys={zoneTypeKeys}
								size="sm"
							>
								<ButtonGroupItem iconLeading={BezierCurve03} id="line">
									Line
								</ButtonGroupItem>
								<ButtonGroupItem iconLeading={Cube01} id="polygon">
									Shape
								</ButtonGroupItem>
							</ButtonGroup>
							{canClearOutline ? (
								<Tooltip placement="top" title={clearOutlineLabel}>
									<Button
										aria-label={clearOutlineLabel}
										color="secondary"
										iconLeading={Trash01}
										onClick={handleClearOutline}
										size="sm"
									/>
								</Tooltip>
							) : null}
						</div>
						<p className="text-sm text-tertiary">
							{isShape ? ZONE_TYPE_SHAPE_HINT : ZONE_TYPE_LINE_HINT}
						</p>
						{isShape ? (
							<>
								<p className="text-sm font-medium text-primary">Zone outline</p>
								<ButtonGroup
									aria-label="Zone outline mode"
									disallowEmptySelection
									onSelectionChange={handleOutlineModeChange}
									selectedKeys={outlineModeKeys}
									size="sm"
								>
									<ButtonGroupItem iconLeading={Lightning01} id="auto">
										Auto
									</ButtonGroupItem>
									<ButtonGroupItem iconLeading={Edit05} id="custom">
										Custom
									</ButtonGroupItem>
								</ButtonGroup>
								<p
									className={
										editDraft.boundaryMode === "custom" && !hasDrawnOutline
											? "text-sm text-success-primary"
											: "text-sm text-tertiary"
									}
								>
									{editDraft.boundaryMode === "custom" && !hasDrawnOutline
										? ZONE_BOUNDARY_CUSTOM_DRAW_HINT
										: OUTLINE_MODE_HINT[editDraft.boundaryMode]}
								</p>
							</>
						) : null}
						{isShape &&
						editDraft.boundaryMode === "custom" &&
						editDraft.boundary ? (
							<Checkbox
								hint={ZONE_BOUNDARY_MOVE_SENSORS_HINT}
								isSelected={moveSensorsWithOutline}
								label={ZONE_BOUNDARY_MOVE_SENSORS_LABEL}
								onChange={onMoveSensorsWithOutlineChange}
								size="sm"
							/>
						) : null}
						{isShape && editDraft.boundaryMode === "custom" ? (
							isBoundarySelfIntersecting ? (
								<p className="text-sm text-error-primary">
									{ZONE_BOUNDARY_SELF_INTERSECTION_MESSAGE}
								</p>
							) : showSensorsOutsideWarning ? (
								<div className="flex flex-col gap-2">
									<p className="text-sm text-warning-primary">
										{getZoneBoundarySensorsOutsideMessage(
											sensorsOutsideBoundary.length,
										)}
									</p>
									<ul className="flex flex-col gap-1">
										{sensorsOutsideBoundary.map((sensor) => (
											<li key={sensor.id}>
												<Button
													color="link-color"
													onBlur={() => onHoverOutsideSensor?.()}
													onClick={() => onPreviewOutsideSensor?.(sensor.id)}
													onMouseEnter={() => onHoverOutsideSensor?.(sensor.id)}
													onMouseLeave={() => onHoverOutsideSensor?.()}
													size="sm"
												>
													{toTitleCase(sensor.name)}
												</Button>
											</li>
										))}
									</ul>
									{onExpandBoundaryToIncludeSensors ? (
										<Button
											color="secondary"
											onClick={onExpandBoundaryToIncludeSensors}
											size="sm"
										>
											{ZONE_BOUNDARY_EXPAND_TO_INCLUDE_LABEL}
										</Button>
									) : null}
								</div>
							) : hasDrawnOutline ? (
								<p className="text-sm text-tertiary">
									{ZONE_BOUNDARY_CUSTOM_VERTEX_HINT}
								</p>
							) : null
						) : null}
					</div>
					{showOutlineStyleControls ? (
						<>
							<div className="flex flex-col gap-2">
								<p className="text-sm font-medium text-primary">Zone color</p>
								<ColorPicker.Provider
									onChange={(color) =>
										onDraftChange({ color: color.toString("hex") })
									}
									value={selectedColor}
								>
									<ColorPicker.SavedColors
										colors={swatchColors}
										label="Saved"
										onAdd={() => {
											setPendingCustomColor(selectedColor);
											setIsAddColorModalOpen(true);
										}}
									/>
								</ColorPicker.Provider>
							</div>
							<div className="flex flex-col gap-2">
								<p className="text-sm font-medium text-primary">
									Line width ({selectedBorderWidth} px)
								</p>
								<Slider
									aria-label="Line width"
									formatOptions={{ maximumFractionDigits: 0 }}
									maxValue={MAX_ZONE_BORDER_WIDTH}
									minValue={MIN_ZONE_BORDER_WIDTH}
									onChange={(value) => {
										const next = Array.isArray(value) ? value[0] : value;
										if (typeof next !== "number") return;
										const borderWidth = normalizeZoneBorderWidth(next);
										if (borderWidth === selectedBorderWidth) return;
										onDraftChange({ borderWidth });
									}}
									step={1}
									value={selectedBorderWidth}
								/>
								<p className="text-sm text-tertiary">
									Line and shape stroke, {MIN_ZONE_BORDER_WIDTH}–
									{MAX_ZONE_BORDER_WIDTH} pixels.
								</p>
							</div>
						</>
					) : null}
				</div>
			</EditScrollBody>
			<div className="bg-secondary_subtle flex shrink-0 flex-wrap items-center gap-2 border-t border-secondary px-4 py-3">
				<Button color="secondary" onClick={onCancel} size="sm">
					Cancel
				</Button>
				{hasEdited ? (
					<Button
						color="primary"
						isDisabled={
							isBoundarySelfIntersecting || isUpdatedZonePending || !showDone
						}
						onClick={onSave}
						size="sm"
					>
						{isUpdatedZonePending || !showDone ? "Saving..." : "Done"}
					</Button>
				) : null}
			</div>

			<GeneralModal
				closeOnSubmit={false}
				description="Pick a custom color to add to your saved swatches."
				isOpen={isAddColorModalOpen}
				onCancel={() => setIsAddColorModalOpen(false)}
				onClose={() => setIsAddColorModalOpen(false)}
				onSubmit={() => {
					handleAddCustomColor();
				}}
				submitText="Add color"
				title="Add zone color"
			>
				<ColorPicker.Provider
					onChange={(color) => setPendingCustomColor(color.toString("hex"))}
					value={pendingCustomColor}
				>
					<ColorPicker />
				</ColorPicker.Provider>
			</GeneralModal>
		</div>
	);
};
